/**
 * NotificationBus provides a cross-tab notification system using BroadcastChannel.
 * This replaces PostgreSQL's LISTEN/NOTIFY functionality for SQLite.
 *
 * It allows different parts of the application (including across browser tabs)
 * to subscribe to and emit notifications on named channels.
 */

type NotificationCallback = (payload: string) => void

interface ChannelSubscription {
  channel: BroadcastChannel
  callbacks: Set<NotificationCallback>
}

class NotificationBusImpl {
  private channels: Map<string, ChannelSubscription> = new Map()
  private prefix: string

  constructor(prefix = 'soniq-notify') {
    this.prefix = prefix
  }

  /**
   * Get or create a BroadcastChannel for the given channel name
   */
  private getOrCreateChannel(channelName: string): ChannelSubscription {
    const existingChannel = this.channels.get(channelName)
    if (existingChannel) {
      return existingChannel
    }

    const broadcastChannel = new BroadcastChannel(
      `${this.prefix}:${channelName}`
    )
    const subscription: ChannelSubscription = {
      channel: broadcastChannel,
      callbacks: new Set(),
    }

    broadcastChannel.onmessage = (event: MessageEvent<string>) => {
      subscription.callbacks.forEach((callback) => {
        try {
          callback(event.data)
        } catch (error) {
          console.error(
            `NotificationBus: Error in callback for channel ${channelName}:`,
            error
          )
        }
      })
    }

    this.channels.set(channelName, subscription)
    return subscription
  }

  /**
   * Emit a notification on the specified channel
   * @param channel The channel name to emit on
   * @param payload The payload to send (will be stringified if not already a string)
   */
  notify(channel: string, payload: string): void {
    const subscription = this.getOrCreateChannel(channel)
    // Post to other tabs/workers
    subscription.channel.postMessage(payload)
    // Also notify local subscribers immediately
    subscription.callbacks.forEach((callback) => {
      try {
        callback(payload)
      } catch (error) {
        console.error(
          `NotificationBus: Error in local callback for channel ${channel}:`,
          error
        )
      }
    })
  }

  /**
   * Subscribe to notifications on the specified channel
   * @param channel The channel name to subscribe to
   * @param callback The callback to invoke when a notification is received
   */
  listen(channel: string, callback: NotificationCallback): void {
    const subscription = this.getOrCreateChannel(channel)
    subscription.callbacks.add(callback)
  }

  /**
   * Unsubscribe from notifications on the specified channel
   * @param channel The channel name to unsubscribe from
   * @param callback The callback to remove
   */
  unlisten(channel: string, callback: NotificationCallback): void {
    const subscription = this.channels.get(channel)
    if (!subscription) {
      return
    }

    subscription.callbacks.delete(callback)

    // Clean up the channel if no more subscribers
    if (subscription.callbacks.size === 0) {
      subscription.channel.close()
      this.channels.delete(channel)
    }
  }

  /**
   * Close all channels and clean up resources
   */
  destroy(): void {
    for (const [, subscription] of this.channels) {
      subscription.channel.close()
    }
    this.channels.clear()
  }
}

// Singleton instance for global use
let globalNotificationBus: NotificationBusImpl | null = null

/**
 * Get the global NotificationBus instance
 */
export function getNotificationBus(): NotificationBusImpl {
  if (!globalNotificationBus) {
    globalNotificationBus = new NotificationBusImpl()
  }
  return globalNotificationBus
}

/**
 * Create a new NotificationBus instance with a custom prefix
 * Useful for testing or isolated contexts
 */
export function createNotificationBus(prefix?: string): NotificationBusImpl {
  return new NotificationBusImpl(prefix)
}

export type NotificationBus = NotificationBusImpl
